// NOMBASE - Main Application
// Lazy-loaded routes + cached queries + non-blocking auth for an
// instantaneous feel on navigation and re-visits.
import React, { Suspense } from 'react';
import { lazyWithRetry } from '@/lib/lazyWithRetry';
import { BrowserRouter, Routes, Route, Navigate, useSearchParams, useLocation } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { QueryClientProvider } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { AppProvider, useApp } from '@/context/AppContext';
import { isSignInNavigationPending } from '@/context/AuthContext';
import { resolveGatewayUrl } from '@/lib/gatewayUrl';
import { DEMAND_ENABLED, AUTOPILOT_ENABLED } from '@/lib/featureFlags';
import AppShell from '@/components/layout/AppShell';
import { AutopilotProvider } from '@/features/autopilot/AutopilotContext';
import AutopilotCursor from '@/components/autopilot/AutopilotCursor';
// OnboardingGate import removed — provisioning is owned by the front door.
import ClientPerfReporter from '@/components/perf/ClientPerfReporter';
import { Toaster } from '@/components/ui/sonner';
import { ConfirmDeleteProvider } from '@/components/ui/confirm-delete-dialog';
import { queryClient, persister } from '@/lib/queryClient';

import NotFound from '@/components/common/NotFound';
import ContactFAB from '@/components/common/ContactFAB';
import { legacyRedirectRoutes } from '@/routes/legacyRedirects';

import '@/App.css';

// ===== Lazy-loaded pages =====
// Every route below is code-split. The initial bundle only contains the
// shell + landing; everything else is fetched on demand. This drastically
// reduces Time-to-Interactive on first load.

// Brand Pages
const BrandDashboard = lazyWithRetry(() => import('@/pages/brand/BrandDashboard'));
const BrandProfileSetup = lazyWithRetry(() => import('@/pages/brand/BrandProfileSetup'));
const BrandNetwork = lazyWithRetry(() => import('@/pages/brand/BrandNetwork'));
const NetworkGraphPage = lazyWithRetry(() => import('@/pages/brand/NetworkGraphPage'));
const BrandProduct = lazyWithRetry(() => import('@/pages/brand/BrandProduct'));
const PlanPage = lazyWithRetry(() => import('@/pages/brand/PlanPage'));
const DemandSection = lazyWithRetry(() => import('@/pages/demand/DemandSection'));
const AutopilotSection = lazyWithRetry(() => import('@/pages/autopilot/AutopilotSection'));
// `ProductionPage`, `AnnualPlanPage`, and `OperationsPage` were removed
// in the Operations v2 surgical cleanup — the new `/plan` calendar
// replaces all three.
const CreateProductionRun = lazyWithRetry(() => import('@/pages/brand/CreateProductionRun'));
const ProductionRunDetail = lazyWithRetry(() => import('@/pages/brand/ProductionRunDetail'));
const EditProductionRun = lazyWithRetry(() => import('@/pages/brand/EditProductionRun'));
// `ProcurementPage` retired — its three tabs were folded into:
//   `?tab=orders` → /buy, `?tab=inbound` → /receive, `?tab=reconcile` → /reconcile.
// `ProcurementRedirect` (defined below) handles deep-link compatibility.
const PurchaseOrderDetail = lazyWithRetry(() => import('@/pages/brand/PurchaseOrderDetail'));

// `/operations/procurement?tab=…` is retired in Operations v2. This tiny
// adapter preserves deep-links from old bookmarks, lifecycle events, and
// outbound emails by routing every legacy tab to its new home:
//   ?tab=orders     → /buy
//   ?tab=inbound    → /receive
//   ?tab=reconcile  → /reconcile
//   (anything else) → /buy
function ProcurementRedirect() {
  const [params] = useSearchParams();
  const tab = (params.get('tab') || '').toLowerCase();
  const dest = tab === 'inbound' || tab === 'receiving' ? '/receive'
    : tab === 'reconcile' || tab === 'reconciliation' ? '/reconcile'
    : '/buy';
  return <Navigate to={dest} replace />;
}
// Operations v2 Phase 2 — clean-slate Buy page on top of the MRP spine.
const BuyPage = lazyWithRetry(() => import('@/pages/Buy'));
// Step 8 — mock-recall drill (Tier D verticals).
const MockRecallPage = lazyWithRetry(() => import('@/pages/operations/MockRecall'));
// Step 9 — integrations + outbound event bus (Tier E).
const IntegrationsPage = lazyWithRetry(() => import('@/pages/settings/Integrations'));
// Dev-only UI grammar gallery (docs/ENTERPRISE_UI_SPEC.md step 1) — the
// import only resolves in development; production builds never register it.
const UiGrammarGallery = process.env.NODE_ENV === 'development'
  ? lazyWithRetry(() => import('@/pages/dev/UiGrammar'))
  : null;
// Operations v2 Phase 3a — Plan calendar (three-track horizontal).
const PlanCalendarPage = lazyWithRetry(() => import('@/pages/Plan'));
// Operations v2 Phase 4 — mobile-first floor app.
const ReceivePage = lazyWithRetry(() => import('@/pages/Receive'));
// Operations v2 fold — standalone reconciliation page replacing
// /operations/procurement?tab=reconcile.
const ReconcilePage = lazyWithRetry(() => import('@/pages/Reconcile'));
// Phase 9 — UOM Hygiene / Audit dashboard.
const UomAuditPage = lazyWithRetry(() => import('@/pages/UomAuditPage'));
const CreateFormula = lazyWithRetry(() => import('@/pages/brand/CreateFormula'));
const FormulaDetail = lazyWithRetry(() => import('@/pages/brand/FormulaDetail'));
const VariantDetailPage = lazyWithRetry(() => import('@/pages/brand/VariantDetailPage'));
const InventoryDetailPage = lazyWithRetry(() => import('@/pages/brand/InventoryDetail'));
const SupplierDetailPage = lazyWithRetry(() => import('@/features/network/suppliers/components/SupplierDetailPage'));
const ManufacturerDetailPage = lazyWithRetry(() => import('@/features/network/manufacturers/components/ManufacturerDetailPage'));
const IngredientDetailPage = lazyWithRetry(() => import('@/features/network/ingredients/components/IngredientDetailPage'));
const BrandProductDetailPage = lazyWithRetry(() => import('@/pages/brand/product/ProductDetailPage'));
const PackagingDetailPage = lazyWithRetry(() => import('@/features/network/packaging/components/PackagingDetailPage'));
const DocumentsHub = lazyWithRetry(() => import('@/pages/DocumentsHub'));

// Supplier Pages — REMOVED (brand-only MVP, Feb 2026). The supplier /
// co-manufacturer / flavor-house / packaging-supplier persona surfaces
// live in separate apps now.

// Requests / RFQ / marketplace pages — REMOVED (brand-only MVP; sourcing
// is a separate system). Only the formulation workspace remains.
const DevelopmentWorkspace = lazyWithRetry(() => import('@/pages/requests/DevelopmentWorkspace'));

// Admin pages live in the standalone Admin Portal — never rendered here.
// The `/admin/*` route group was removed Feb 2026; super_admin no longer
// bypasses tenant-scoped UI in this bundle.

// Tiny, non-intrusive fallback shown while a lazy chunk downloads.
// Uses the same look as auth-hydrate so users don't see theme shift.
const RouteFallback = () => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

// ── Route-level error boundary ──────────────────────────────────────────
// A render error in ANY single page must NEVER black-screen the whole SPA
// (which is what happens when it bubbles to the root Sentry boundary in
// index.js). This catches it, keeps the shell + navigation alive, and shows
// a localized, navigable fallback that ALSO surfaces the underlying error so
// it can be diagnosed. Auto-resets when the route changes so navigating away
// always recovers.
function RouteErrorFallback({ error }) {
  const detail = String((error && (error.message || error.toString())) || 'Unknown error');
  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6" data-testid="route-error-fallback">
      <div className="max-w-lg w-full text-center bg-white rounded-2xl border border-gray-200 p-8 shadow-sm">
        <div className="w-14 h-14 mx-auto rounded-2xl bg-rose-100 text-rose-600 flex items-center justify-center mb-4">
          <AlertTriangle className="w-7 h-7" />
        </div>
        <h2 className="text-lg font-semibold text-gray-900 mb-1">This page hit a snag</h2>
        <p className="text-sm text-gray-500 mb-4">
          The rest of the app is fine — head back and keep working. Our team has been notified.
        </p>
        <pre
          className="text-left text-[11px] font-mono text-rose-700 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2 mb-5 overflow-auto max-h-32 whitespace-pre-wrap"
          data-testid="route-error-detail"
        >
          {detail}
        </pre>
        <div className="flex items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => window.history.back()}
            data-testid="route-error-back"
            className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-white bg-slate-900 hover:bg-black px-4 py-2 rounded-full transition-colors"
          >
            Go back
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            data-testid="route-error-reload"
            className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 px-4 py-2 rounded-full transition-colors"
          >
            Reload
          </button>
        </div>
      </div>
    </div>
  );
}

class RouteErrorBoundaryInner extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[RouteErrorBoundary]', error, info && info.componentStack);
  }

  componentDidUpdate(prevProps) {
    // Navigating to a different route clears a stale error so the user is
    // never stuck on the fallback after moving on.
    if (prevProps.routeKey !== this.props.routeKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  render() {
    if (this.state.error) {
      return <RouteErrorFallback error={this.state.error} />;
    }
    return this.props.children;
  }
}

function RouteErrorBoundary({ children }) {
  const location = useLocation();
  return (
    <RouteErrorBoundaryInner routeKey={location.pathname}>
      {children}
    </RouteErrorBoundaryInner>
  );
}

// Dashboard Router — brand-only since the Feb 2026 MVP simplification.
// Kept as a one-line wrapper so callsites that import DashboardRouter
// don't need to change.
const DashboardRouter = () => <BrandDashboard />;

// Protected route for brand-only pages. In the brand-only MVP every
// authenticated user IS a brand, so this guard is effectively a
// no-op — kept so existing route declarations don't need to change.
const BrandOnly = ({ children }) => {
  const { isBrand } = useApp();
  if (!isBrand) {
    return <Navigate to="/" replace />;
  }
  return children;
};

// SupplierOnly guard — REMOVED (Feb 2026). Supplier persona surfaces
// no longer exist in this app; any route still wrapped in it should
// be deleted, not gated.

// `UnauthenticatedBootstrap`, `FrontDoorRedirectSpinner`, and the
// embedded `/sign-in` / `/sign-up` routes were removed May 2026. The
// app now relies on the backend's `/api/auth/dev-bootstrap` shim to
// mint a session cookie on the first 401 from `/api/auth/me`, so
// users land directly on the dashboard with no sign-in screen.

// Main App Routes
export const AppRoutes = () => {
  const { isBrand, isAuthenticated, isLoading, refreshIdentity, authError } = useApp();

  // Defensive boot supervisor.
  //
  // Phase ladder (driven by how long `isLoading` has been true):
  //   • 0-12s   → 'initial'   : silent spinner (no scary UI). Covers
  //                             the warm-pod happy path entirely.
  //   • 12-45s  → 'retrying'  : calm "Connecting…" card + background
  //                             auto-retry of `refreshIdentity` every
  //                             6s. Covers prod scale-to-zero
  //                             cold starts (typical 15-30s).
  //   • >45s    → 'failed'    : manual-Refresh card. The probe was
  //                             retried ~5 times with no recovery —
  //                             at this point it's almost certainly
  //                             a real outage, not a cold start.
  //
  // Why this matters: previously the 12s threshold immediately surfaced
  // the alarming "!" splash on every prod cold start, which is the
  // 95th-percentile case and not actually broken. The new ladder
  // makes that path invisible-to-user.
  const [bootPhase, setBootPhase] = React.useState('initial');
  React.useEffect(() => {
    if (!isLoading) { setBootPhase('initial'); return; }
    const toRetrying = setTimeout(() => setBootPhase('retrying'), 12_000);
    const toFailed   = setTimeout(() => setBootPhase('failed'), 45_000);
    return () => { clearTimeout(toRetrying); clearTimeout(toFailed); };
  }, [isLoading]);

  // While in 'retrying', poke `refreshIdentity` every 6s. The hook
  // itself wraps everything in try/catch and toggles `isLoading` in
  // a `finally`, so a slow probe will simply hold `isLoading=true`
  // and we'll fire again. A successful probe flips `isLoading=false`
  // (cleanup runs, no further pokes).
  React.useEffect(() => {
    if (bootPhase !== 'retrying' || typeof refreshIdentity !== 'function') return;
    // Kick once immediately when we enter the retry phase so users
    // don't sit on the "Connecting…" card for a full 6s before the
    // first re-probe.
    try { refreshIdentity(); } catch { /* swallowed in hook */ }
    const id = setInterval(() => {
      try { refreshIdentity(); } catch { /* */ }
    }, 6_000);
    return () => clearInterval(id);
  }, [bootPhase, refreshIdentity]);

  // Gateway sign-in redirect. Runs unconditionally on every render
  // (hooks rule) but only ACTS when boot is finished AND the user
  // is unauthenticated AND no other sign-in / sign-out navigation
  // is already in flight. The in-flight guard is critical because
  // `logout()` does:
  //   setIsAuthed(false)            // queues React re-render
  //   navigateToSignIn('sign_out')  // queues browser nav to /sign-out
  // The browser commits `window.location.assign` asynchronously, so by
  // the time React flushes the re-render, this effect would fire and
  // call `assign(gateway/sign-in)` — which arrives AFTER the queued
  // sign-out and wins. The guard makes us skip the second assign so
  // the sign-out URL holds.
  React.useEffect(() => {
    if (isLoading || isAuthenticated) return;
    if (isSignInNavigationPending()) return; // logout / re-auth already navigating
    // CRITICAL: if a Gateway token just arrived AND was rejected by
    // /api/sso, do NOT bounce back to the Gateway. That creates a
    // tight loop (Gateway → MRP → exchange-fail → Gateway → ...). We
    // render an error card below with the specific rejection reason
    // so the user can take a deliberate action (re-sign-in, contact
    // ops, etc.). See AuthContext.loadIdentity for the capture site.
    if (authError) return;
    // Resolve via the shared helper so this matches AuthContext exactly.
    // See `lib/gatewayUrl.js` for the resolution-order contract and the
    // 2026-05-26 prod-build self-heal write-up.
    const { url: gatewayUrl, source: gwSource } = resolveGatewayUrl();
    const rawEnv = (process.env.REACT_APP_GATEWAY_URL || '').replace(/\/+$/, '');
    if (!gatewayUrl) {
      // Loud-fail diagnostics — silently composing `undefined/sign-in`
      // was the root cause of the 2026-05-26 logout-loop bug. Surface
      // the missing-env condition so any reader (human or agent) sees
      // it without grepping the bundle.
      if (!rawEnv) {
        // eslint-disable-next-line no-console
        console.error(
          '[AppRoutes] REACT_APP_GATEWAY_URL is empty AND host does not'
          + ' start with `mrp.` — cannot redirect to Gateway sign-in. Set'
          + ' the env in /app/frontend/.env (preview) or the deployment'
          + ' Secrets panel + redeploy (prod). CRA bakes REACT_APP_* at'
          + ' build.'
        );
      } else {
        // eslint-disable-next-line no-console
        console.error(
          `[AppRoutes] REACT_APP_GATEWAY_URL (${rawEnv}) points at THIS MRP`
          + ` origin (${window.location.origin}) AND host does not start with`
          + ' `mrp.` — runtime fallback impossible. Expected the Nombase'
          + ' Gateway host (e.g. https://nombaseconnect.com). Skipping redirect'
          + ' to avoid an infinite loop; user will see the static unauth card'
          + ' below.'
        );
      }
      return;
    }
    if (gwSource === 'derived') {
      // Soft-warn: runtime fallback fired. Functional; operator should
      // still fix the Custom Keys panel so future builds bake the value.
      // eslint-disable-next-line no-console
      console.warn(
        `[AppRoutes] REACT_APP_GATEWAY_URL missing/self-pointing — derived`
        + ` Gateway from window.location: ${gatewayUrl}. Update prod Secrets`
        + ' to bake this at build time and silence this warning.'
      );
    }
    const here = window.location.href;
    const target = `${gatewayUrl}/sign-in?return_to=${encodeURIComponent(here)}`;
    window.location.assign(target);
  }, [isLoading, isAuthenticated]);

  // Show nothing while checking auth state (only when we truly have no cached user)
  if (isLoading && bootPhase === 'initial') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-3">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Calm intermediate state — backend is almost certainly cold-starting.
  // Auto-retry is firing in the background; user sees a friendly
  // spinner, not the alarming "!" splash.
  if (isLoading && bootPhase === 'retrying') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-6">
        <div className="max-w-md text-center space-y-4" data-testid="app-boot-retrying">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <h2 className="text-base font-semibold text-foreground">Connecting…</h2>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Waking up your workspace. This usually takes a few seconds.
          </p>
        </div>
      </div>
    );
  }

  if (isLoading && bootPhase === 'failed') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-6">
        <div className="max-w-md text-center space-y-4" data-testid="app-boot-timeout">
          <div className="w-10 h-10 mx-auto rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-base font-bold">!</div>
          <h2 className="text-lg font-semibold text-foreground">Taking longer than usual…</h2>
          <p className="text-sm text-muted-foreground leading-relaxed">
            We couldn&apos;t finish loading your workspace. This is usually a
            cold-start on the backend or a momentary network blip.
          </p>
          <div className="flex items-center justify-center gap-2 pt-2">
            <button
              onClick={() => window.location.reload()}
              data-testid="app-boot-retry"
              className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-slate-700
                         bg-white border border-slate-200 hover:bg-slate-50 hover:border-slate-300
                         px-3 py-1.5 rounded-full transition-colors"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>
    );
  }

  // `!isAuthenticated && !isLoading` reaches the JSX below only when
  // the Gateway redirect (effect above) hasn't fired yet (e.g. one
  // commit window) OR when no `REACT_APP_GATEWAY_URL` is configured.
  // The card is the user's visible state during that interval and the
  // permanent fallback for un-configured preview pods.
  if (!isAuthenticated) {
    // Resolve once so the rendering branch + the Retry handler agree
    // on the same source-of-truth (env OR derived `mrp.` strip).
    const { url: gatewayUrl } = resolveGatewayUrl();

    // ── Loop-break: Gateway SSO token was rejected ──────────────────
    // When we arrived from the Gateway with `?token=` and the backend
    // rejected it, do NOT silently re-redirect to the Gateway — that
    // creates an infinite loop. Show the actual rejection reason so
    // the operator can act on it (most common: `token_expired` from a
    // stale tab, `MRP_GATEWAY_SHARED_SECRET is not configured` from a
    // deploy that lost its env var, `token_audience_mismatch` from a
    // Gateway-side misconfiguration).
    if (authError) {
      const friendlyByCode = {
        'token_expired':            'Your sign-in link expired. Please sign in again.',
        'token_invalid':            'The sign-in token failed verification. Please sign in again.',
        'token_replayed':           'This sign-in link was already used. Please sign in again.',
        'token_audience_mismatch':  'Sign-in token was issued for a different service. Contact support.',
        'token_issuer_mismatch':    'Sign-in token was issued by an unrecognised authority. Contact support.',
        'token_required':           'No sign-in token was supplied. Please sign in again.',
        'sso_not_configured':       'The MRP server is missing its Gateway secret. This is a deploy-side configuration issue — contact support.',
      };
      const raw = (authError.error || '').toString();
      const code = raw.split(':')[0] || raw;
      const friendly = friendlyByCode[code]
        || (raw.includes('MRP_GATEWAY_SHARED_SECRET')
              ? 'The MRP server is missing its Gateway secret. This is a deploy-side configuration issue — contact support.'
              : `Sign-in failed: ${raw || 'unknown error'}.`);
      return (
        <div className="min-h-screen flex items-center justify-center bg-background px-6">
          <div className="max-w-md text-center space-y-4" data-testid="app-auth-error">
            <div className="w-10 h-10 mx-auto rounded-full bg-rose-100 text-rose-700 flex items-center justify-center text-base font-bold">!</div>
            <h2 className="text-lg font-semibold text-foreground">Sign-in failed</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">{friendly}</p>
            <p className="text-[11px] font-mono text-muted-foreground bg-slate-50 border border-slate-200 rounded-md px-2 py-1 inline-block" data-testid="app-auth-error-code">
              {code || 'unknown'}
            </p>
            <div className="flex items-center justify-center gap-2 pt-2">
              {gatewayUrl && (
                <button
                  type="button"
                  onClick={() => {
                    // Manual re-auth — user has SEEN the error and
                    // explicitly clicked through, so we hand control
                    // back to the Gateway with a fresh return_to.
                    const here = window.location.origin + window.location.pathname;
                    window.location.assign(`${gatewayUrl}/sign-in?return_to=${encodeURIComponent(here)}`);
                  }}
                  data-testid="app-auth-error-signin"
                  className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-white
                             bg-slate-900 hover:bg-black
                             px-3 py-1.5 rounded-full transition-colors"
                >
                  Sign in again
                </button>
              )}
              <button
                type="button"
                onClick={() => window.location.reload()}
                data-testid="app-auth-error-reload"
                className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-slate-700
                           bg-white border border-slate-200 hover:bg-slate-50 hover:border-slate-300
                           px-3 py-1.5 rounded-full transition-colors"
              >
                Reload
              </button>
            </div>
          </div>
        </div>
      );
    }

    // ── Happy path: Gateway IS resolvable ────────────────────────────
    // The unauth-bounce useEffect above is about to commit the
    // ``window.location.assign(${gateway}/sign-in?...)`` navigation.
    // We're rendering for at most one paint frame; the red "!" alert
    // card we used to show here reads as an error in that tiny window
    // and scared production users into thinking the app was broken.
    // Render a neutral cross-fade-friendly loader instead — same
    // visual weight as the global ``isLoading`` skeleton, no error
    // semiotics. If the redirect actually fails to fire, the original
    // App.js diagnostic logs (see useEffect above) carry the
    // operator-facing detail; the end-user surface stays calm.
    if (gatewayUrl) {
      return (
        <div
          className="min-h-screen flex items-center justify-center bg-background"
          data-testid="app-auth-redirecting"
        >
          <div className="flex items-center gap-3 text-muted-foreground">
            <div
              className="w-4 h-4 rounded-full border-2 border-slate-300 border-t-slate-700 animate-spin"
              aria-hidden="true"
            />
            <span className="text-sm">Loading…</span>
          </div>
        </div>
      );
    }

    // ── Misconfig path: NO Gateway resolvable ───────────────────────
    // Reached only on preview pods where REACT_APP_GATEWAY_URL is
    // unset AND the host isn't on the ``mrp.`` family — i.e. there is
    // genuinely no Gateway to redirect to. This IS an actionable
    // error state, so the alert card stays. Operator sees it (with
    // the fix instructions inline); production users never do.
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-6">
        <div className="max-w-md text-center space-y-4" data-testid="app-auth-unavailable">
          <div className="w-10 h-10 mx-auto rounded-full bg-rose-100 text-rose-700 flex items-center justify-center text-base font-bold">!</div>
          <h2 className="text-lg font-semibold text-foreground">Sign-in unavailable</h2>
          <p className="text-sm text-muted-foreground leading-relaxed">
            The backend couldn&apos;t authenticate this device and no
            Gateway URL is configured. Set <code className="bg-slate-100 px-1 rounded">REACT_APP_GATEWAY_URL</code>
            in the frontend env to enable automatic sign-in redirects.
          </p>
          <div className="flex items-center justify-center gap-2 pt-2">
            <button
              onClick={() => window.location.reload()}
              data-testid="app-auth-unavailable-retry"
              className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-slate-700
                         bg-white border border-slate-200 hover:bg-slate-50 hover:border-slate-300
                         px-3 py-1.5 rounded-full transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    // OnboardingGate intentionally removed (Feb 2026) — provisioning
    // and account creation flow happens at Clerk sign-up directly.
    // Users land here already authorized; we no longer collect
    // company/role onboarding details inside the main app.
    <AutopilotProvider>
    <AutopilotCursor />
    <AppShell>
      <ClientPerfReporter />
      <Suspense fallback={<RouteFallback />}>
      <RouteErrorBoundary>
      <Routes>
          {/* Dashboard - tenant-aware */}
          <Route path="/" element={<DashboardRouter />} />

          {/* Admin routes were removed — system admin lives in the
              standalone Admin Portal. Any direct `/admin/*` link now
              falls through to the global 404 below. */}

          {/* Brand-only routes */}
          {/* /search is deprecated — the only search surface is the RFQ Marketplace */}
          <Route path="/search" element={<Navigate to="/requests/marketplace" replace />} />

          {/* Brand Network — Suppliers, Manufacturers, Materials
              Catalog. Phase-10-reversal: Materials moved BACK from
              /product because procurement (multi-source pricing,
              savings, bulk import) is fundamentally network-y. The
              BrandNetwork component itself absorbs the legacy
              `?tab=ingredients|packaging|savings|import` URL forms
              and rewrites them in place. */}
          <Route path="/network" element={
            <BrandOnly><BrandNetwork /></BrandOnly>
          } />

          {/* Demand — decision-first launch/production/purchase/margin flows.
              Phase 1 (sandbox): Retail Launch Verdict. Nested routes live in
              the DemandSection host. */}
          <Route path="/demand/*" element={
            DEMAND_ENABLED ? <BrandOnly><DemandSection /></BrandOnly> : <Navigate to="/" replace />
          } />

          {/* Autopilot — production-feasibility / stockout recommendation feed.
              Flag-gated (AUTOPILOT_ENABLED), brand-only, nested in AutopilotSection. */}
          <Route path="/autopilot/*" element={
            AUTOPILOT_ENABLED ? <BrandOnly><AutopilotSection /></BrandOnly> : <Navigate to="/" replace />
          } />

          {/* ── Product Management → renamed to /products (plural) ──
              The legacy /product paths now redirect to /products and
              preserve query/hash so deep-links from emails keep
              working. */}
          <Route path="/products" element={
            <BrandOnly><BrandProductWithLegacyRedirects /></BrandOnly>
          } />
          <Route path="/products/formulas/create" element={
            <BrandOnly><CreateFormula /></BrandOnly>
          } />
          <Route path="/products/formulas/:formulaId/edit" element={
            <BrandOnly><FormulaDetail /></BrandOnly>
          } />
          <Route path="/products/formulas/:formulaId" element={
            <BrandOnly><FormulaDetail /></BrandOnly>
          } />
          <Route path="/products/variants/create" element={
            <Navigate to="/products?tab=variants&action=create" replace />
          } />
          <Route path="/products/variants/create/solid-sku" element={
            <Navigate to="/products?tab=variants&action=create" replace />
          } />
          <Route path="/products/variants/create/variety-pack" element={
            <Navigate to="/products?tab=variants&action=create" replace />
          } />
          <Route path="/products/variants/:variantId" element={
            <BrandOnly><VariantDetailPage /></BrandOnly>
          } />
          <Route path="/products/inventory/:inventoryItemId" element={
            <BrandOnly><InventoryDetailPage /></BrandOnly>
          } />
          {/* Marketplace product detail — accessible to all authenticated users */}
          <Route path="/products/:productId" element={<BrandProductDetailPage />} />

          {/* Legacy /product* paths — kept as transparent SPA redirects
              so prod bookmarks and external links continue to work.
              Block extracted to `/src/routes/legacyRedirects.jsx` —
              see also `LegacyRewriteRedirect`. */}
          {/* (the /product/* portion of the legacy block lives below
              alongside every other legacy slug — single source of truth) */}

          {/* Development — ChatGPT-style formulation workspace */}
          <Route path="/development" element={
            <BrandOnly><DevelopmentWorkspace /></BrandOnly>
          } />

          {/* Operations — unified Plan / Production / Procurement surface.
              For BRAND tenants this is the new tabbed wrapper. For
              SUPPLIER co-manufacturers, /operations resolves to the
              legacy CoMfgOperations page (via the role-gated route below).
              The legacy /plan, /production, /procurement top-level routes
              redirect into the corresponding tab. Detail routes
              (/production/:id, /procurement/purchase-orders/:id) stay
              standalone — they're drill-downs, not top-level nav. */}
          {/* Operations v2 — `/operations/*` legacy paths now redirect to
              the new clean-slate workflow. ProcurementPage retired. */}
          {isBrand && (
            <Route path="/documents" element={
              <BrandOnly><DocumentsHub /></BrandOnly>
            } />
          )}
          {isBrand && (
            <Route path="/operations/runs/:id" element={
              <ProductionRunDetail />
            } />
          )}
          {isBrand && (
            <Route path="/operations/procurement" element={
              <ProcurementRedirect />
            } />
          )}
          {isBrand && (
            <Route path="/operations/uom-audit" element={
              <BrandOnly><UomAuditPage /></BrandOnly>
            } />
          )}
          {isBrand && (
            <Route path="/operations/mock-recall" element={
              <BrandOnly><MockRecallPage /></BrandOnly>
            } />
          )}
          {isBrand && (
            <Route path="/settings/integrations" element={
              <BrandOnly><IntegrationsPage /></BrandOnly>
            } />
          )}
          {isBrand && (
            <Route path="/operations/:tab" element={
              <Navigate to="/plan" replace />
            } />
          )}

          {/* Phase 9 — UOM Hygiene / Audit dashboard. */}

          {/* Production Management - Production Runs */}
          <Route path="/plan" element={
            <BrandOnly><PlanCalendarPage /></BrandOnly>
          } />
          <Route path="/plan/drafts" element={
            <BrandOnly><PlanPage /></BrandOnly>
          } />
          <Route path="/production" element={
            <BrandOnly><Navigate to="/plan" replace /></BrandOnly>
          } />
          {/* Legacy Annual Plan — retired in Operations v2. Deep-links land on /plan. */}
          <Route path="/annual-plan" element={
            <BrandOnly><Navigate to="/plan" replace /></BrandOnly>
          } />
          <Route path="/production/create" element={
            <BrandOnly><CreateProductionRun /></BrandOnly>
          } />
          <Route path="/production/:id" element={
            <BrandOnly><ProductionRunDetail /></BrandOnly>
          } />
          <Route path="/production/:id/edit" element={
            <BrandOnly><EditProductionRun /></BrandOnly>
          } />

          {/* Operations v2 Phase 2 — clean-slate Buy page. */}
          {process.env.NODE_ENV === 'development' && UiGrammarGallery && (
            <Route path="/dev/ui-grammar" element={<UiGrammarGallery />} />
          )}
          <Route path="/buy" element={
            <BrandOnly><BuyPage /></BrandOnly>
          } />
          {/* Operations v2 Phase 4 — mobile-first floor app. */}
          <Route path="/receive" element={
            <BrandOnly><ReceivePage /></BrandOnly>
          } />
          {/* Operations v2 fold — standalone reconciliation page. */}
          <Route path="/reconcile" element={
            <BrandOnly><ReconcilePage /></BrandOnly>
          } />

          {/* Procurement Management - Purchase Orders, Receiving, Reconciliation */}
          <Route path="/procurement" element={
            <BrandOnly><LegacyProcurementRedirect /></BrandOnly>
          } />
          <Route path="/procurement/purchase-orders/:id" element={
            <BrandOnly><PurchaseOrderDetail /></BrandOnly>
          } />

          {/* Supplier Detail Page */}
          {/* Supplier Detail Page */}
          <Route path="/network/graph" element={
            <BrandOnly><NetworkGraphPage /></BrandOnly>
          } />

          {/* Canonical: plural detail routes (uniform slug convention).
              Their legacy singular siblings (e.g. /network/supplier/:id)
              live in `/src/routes/legacyRedirects.jsx`. */}
          <Route path="/network/suppliers/:supplierId" element={
            <BrandOnly><SupplierDetailPage /></BrandOnly>
          } />

          {/* Manufacturer Detail Page (canonical plural) */}
          <Route path="/network/manufacturers/:manufacturerId" element={
            <BrandOnly><ManufacturerDetailPage /></BrandOnly>
          } />

          {/* Inventory Detail — lives under /network so clicking an inventory
              row stays in the Network section (siblings: suppliers/manufacturers)
              instead of jumping to the Products nav. */}
          <Route path="/network/inventory/:inventoryItemId" element={
            <BrandOnly><InventoryDetailPage /></BrandOnly>
          } />

          {/* Ingredient Detail Page (canonical: drop /brand/ orphan prefix) */}
          <Route path="/ingredients/:ingredientId" element={
            <BrandOnly><IngredientDetailPage /></BrandOnly>
          } />

          {/* Packaging Detail Page (packaging is uncountable — singular slug is canonical) */}
          <Route path="/packaging/:packagingId" element={
            <BrandOnly><PackagingDetailPage /></BrandOnly>
          } />

          {/* Legacy brand redirects → consolidated Network surface. The
              sourcing / RFQ / marketplace / supplier-storefront surfaces
              were REMOVED (brand-only MVP) — they live in the separate
              sourcing system now. Old deep-links land on /network or /buy;
              the storefront URLs fall through to 404. */}
          <Route path="/suppliers" element={
            <BrandOnly><Navigate to="/network?tab=suppliers" replace /></BrandOnly>
          } />
          <Route path="/my-manufacturers" element={
            <BrandOnly><Navigate to="/network?tab=manufacturers" replace /></BrandOnly>
          } />
          <Route path="/co-manufacturers" element={
            <BrandOnly><Navigate to="/network?tab=manufacturers" replace /></BrandOnly>
          } />

          {/* Legacy slug redirects (singular → plural; /brand/ → root). */}
          {legacyRedirectRoutes()}

          {/* RFQ / marketplace removed — redirect old /requests* links to /buy. */}
          <Route path="/requests" element={<Navigate to="/buy" replace />} />
          <Route path="/requests/*" element={<Navigate to="/buy" replace />} />

          {/* Brand setup — unified single-page wizard. */}
          <Route path="/setup/company"   element={<BrandOnly><BrandProfileSetup /></BrandOnly>} />
          <Route path="/setup/addresses" element={<BrandOnly><BrandProfileSetup /></BrandOnly>} />
          <Route path="/setup/team"      element={<BrandOnly><BrandProfileSetup /></BrandOnly>} />
          <Route path="/setup/workflow"  element={<BrandOnly><BrandProfileSetup /></BrandOnly>} />

          {/*
            Supplier-persona routes removed (Feb 2026 brand-only MVP):
            /ingredients, /flavors, /packaging, /services,
            /capability-profile, /operations (co-mfg), /customers,
            /pipeline, /scorecard, /mfg-analytics, /quality,
            /client-portal, /capacity, /orders (supplier),
            /supplier/orders, /shipments, /storefront,
            /setup/identity, /setup/public, /setup/ingredients,
            /setup/warehouses, /setup/routing,
            /settings/supplier-preferences.
            Any deep-link to one of these now falls through to the
            404 route below.
          */}

          {/* 404 */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </RouteErrorBoundary>
      </Suspense>
    </AppShell>
    </AutopilotProvider>
  );
};

// Preserves any query string when redirecting from the legacy /procurement
// path to the canonical /operations/procurement. Without this, deep links
// like /procurement?tab=receiving silently lose their tab selection.
function LegacyProcurementRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/operations/procurement${search || ''}`} replace />;
}

// Phase-10-reversal — Materials Catalog AND Inventory moved from
// /product back to /network. Old bookmarks of the form
// `/product?tab=materials&sub=*` or `/product?tab=inventory&sub=*`
// are transparently rewritten so emails, shared links, and old
// browser history keep working.
function BrandProductWithLegacyRedirects() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const tab = params.get('tab');
  if (tab === 'materials') {
    const sub = params.get('sub') || 'ingredients';
    return <Navigate to={`/network?tab=materials&sub=${sub}`} replace />;
  }
  if (tab === 'inventory') {
    const sub = params.get('sub') || 'ingredients';
    return <Navigate to={`/network?tab=inventory&sub=${sub}`} replace />;
  }
  return <BrandProduct />;
}

function App() {
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{ persister, maxAge: 24 * 60 * 60 * 1000 /* 24h */ }}
    >
      <AppProvider>
        <ConfirmDeleteProvider>
          <BrowserRouter>
            <Suspense fallback={<div className="min-h-screen" />}>
              <Routes>
                {/* Legacy deep-links to in-app `/login`, `/register`,
                    `/sso-callback` fall through to AppRoutes, which
                    routes unauthenticated visitors to the embedded
                    `/sign-in` (Clerk's <SignIn/>). The previous
                    cross-origin gateway architecture was retired
                    May 2026. */}
                <Route path="/welcome" element={<Navigate to="/" replace />} />

                {/* All other routes use AppShell */}
                <Route path="/*" element={<AppRoutes />} />
              </Routes>
            </Suspense>
            <ContactFAB />
            <Toaster position="top-right" />
          </BrowserRouter>
        </ConfirmDeleteProvider>
    </AppProvider>
  </PersistQueryClientProvider>
  );
}

export default App;
